/**
 * Widget ECharts types — terms / nested terms aggregation (key + doc_count).
 * Excludes geo, graph, lines, pictorialBar (gallery), 3D/GL per product scope.
 */

export type ChartOrientation = "vertical" | "horizontal";

export type ChartFamily =
  | "cartesian"
  | "circular"
  | "radial"
  | "hierarchical"
  | "matrix"
  | "flow"
  | "statistical";

export interface ChartTypeDefinition {
  id: string;
  family: ChartFamily;
  seriesType: string;
  supportsOrientation: boolean;
  labelEn: string;
  labelTr: string;
}

export const WIDGET_CHART_TYPES: ChartTypeDefinition[] = [
  { id: "bar", family: "cartesian", seriesType: "bar", supportsOrientation: true, labelEn: "Bar", labelTr: "Sütun" },
  { id: "line", family: "cartesian", seriesType: "line", supportsOrientation: true, labelEn: "Line", labelTr: "Çizgi" },
  { id: "area", family: "cartesian", seriesType: "line", supportsOrientation: true, labelEn: "Area", labelTr: "Alan" },
  {
    id: "stackedBar",
    family: "cartesian",
    seriesType: "bar",
    supportsOrientation: true,
    labelEn: "Stacked bar",
    labelTr: "Yığılmış sütun",
  },
  {
    id: "stackedLine",
    family: "cartesian",
    seriesType: "line",
    supportsOrientation: true,
    labelEn: "Stacked line",
    labelTr: "Yığılmış çizgi",
  },
  {
    id: "stackedArea",
    family: "cartesian",
    seriesType: "line",
    supportsOrientation: true,
    labelEn: "Stacked area",
    labelTr: "Yığılmış alan",
  },
  {
    id: "mixed",
    family: "cartesian",
    seriesType: "bar",
    supportsOrientation: true,
    labelEn: "Mixed line-bar",
    labelTr: "Karma çizgi-sütun",
  },
  {
    id: "waterfall",
    family: "cartesian",
    seriesType: "bar",
    supportsOrientation: true,
    labelEn: "Waterfall",
    labelTr: "Şelale",
  },
  {
    id: "pictorialBar",
    family: "cartesian",
    seriesType: "pictorialBar",
    supportsOrientation: true,
    labelEn: "Pictorial bar",
    labelTr: "Piktorial sütun",
  },
  {
    id: "scatter",
    family: "cartesian",
    seriesType: "scatter",
    supportsOrientation: false,
    labelEn: "Scatter",
    labelTr: "Saçılım",
  },
  {
    id: "effectScatter",
    family: "cartesian",
    seriesType: "effectScatter",
    supportsOrientation: false,
    labelEn: "Effect scatter",
    labelTr: "Efektli saçılım",
  },
  { id: "pie", family: "circular", seriesType: "pie", supportsOrientation: false, labelEn: "Pie", labelTr: "Pasta" },
  {
    id: "doughnut",
    family: "circular",
    seriesType: "pie",
    supportsOrientation: false,
    labelEn: "Doughnut",
    labelTr: "Halka",
  },
  {
    id: "rosePie",
    family: "circular",
    seriesType: "pie",
    supportsOrientation: false,
    labelEn: "Rose (Nightingale)",
    labelTr: "Gül (Nightingale)",
  },
  { id: "funnel", family: "circular", seriesType: "funnel", supportsOrientation: false, labelEn: "Funnel", labelTr: "Huni" },
  { id: "radar", family: "radial", seriesType: "radar", supportsOrientation: false, labelEn: "Radar", labelTr: "Radar" },
  { id: "gauge", family: "radial", seriesType: "gauge", supportsOrientation: false, labelEn: "Gauge", labelTr: "Gösterge" },
  {
    id: "parallel",
    family: "radial",
    seriesType: "parallel",
    supportsOrientation: false,
    labelEn: "Parallel",
    labelTr: "Paralel",
  },
  {
    id: "sunburst",
    family: "hierarchical",
    seriesType: "sunburst",
    supportsOrientation: false,
    labelEn: "Sunburst",
    labelTr: "Sunburst",
  },
  {
    id: "treemap",
    family: "hierarchical",
    seriesType: "treemap",
    supportsOrientation: false,
    labelEn: "Treemap",
    labelTr: "Treemap",
  },
  { id: "tree", family: "hierarchical", seriesType: "tree", supportsOrientation: false, labelEn: "Tree", labelTr: "Ağaç" },
  {
    id: "heatmap",
    family: "matrix",
    seriesType: "heatmap",
    supportsOrientation: false,
    labelEn: "Heatmap",
    labelTr: "Isı haritası",
  },
  {
    id: "calendarHeatmap",
    family: "matrix",
    seriesType: "heatmap",
    supportsOrientation: false,
    labelEn: "Calendar heatmap",
    labelTr: "Takvim ısı haritası",
  },
  {
    id: "themeRiver",
    family: "flow",
    seriesType: "themeRiver",
    supportsOrientation: false,
    labelEn: "Theme river",
    labelTr: "Theme river",
  },
  { id: "sankey", family: "flow", seriesType: "sankey", supportsOrientation: false, labelEn: "Sankey", labelTr: "Sankey" },
  {
    id: "boxplot",
    family: "statistical",
    seriesType: "boxplot",
    supportsOrientation: false,
    labelEn: "Boxplot",
    labelTr: "Kutu grafik",
  },
  {
    id: "candlestick",
    family: "statistical",
    seriesType: "candlestick",
    supportsOrientation: false,
    labelEn: "Candlestick",
    labelTr: "Mum grafik",
  },
];

export const WIDGET_CHART_TYPE_IDS = new Set(WIDGET_CHART_TYPES.map((c) => c.id));

const ID_ALIASES: Record<string, string> = {
  pictorialbar: "pictorialBar",
  effectscatter: "effectScatter",
  rosepie: "rosePie",
  stackedbar: "stackedBar",
  stackedline: "stackedLine",
  stackedarea: "stackedArea",
  calendarheatmap: "calendarHeatmap",
  themeriver: "themeRiver",
};

export function getChartTypeDef(id: string): ChartTypeDefinition | undefined {
  return WIDGET_CHART_TYPES.find((c) => c.id === id);
}

export function getSwitchableChartTypes(currentId: string): ChartTypeDefinition[] {
  const current = getChartTypeDef(currentId);
  if (!current) return WIDGET_CHART_TYPES;
  return WIDGET_CHART_TYPES.filter((c) => c.family === current.family);
}

export function normalizeChartTypeId(raw?: string): string {
  const v = String(raw || "bar").trim();
  const lower = v.toLowerCase();
  if (ID_ALIASES[lower]) return ID_ALIASES[lower];
  if (WIDGET_CHART_TYPE_IDS.has(v)) return v;
  if (WIDGET_CHART_TYPE_IDS.has(lower)) return lower;
  return "bar";
}
